import { cn } from '../../utils/cn';
import { useI18n } from '@bravemove/i18n/react/core';

export interface ProgressBarProps {
  /** When omitted, the bar shows an indeterminate shimmer. */
  readonly value?: number;
  readonly max?: number;
  readonly size?: 'sm' | 'md';
  readonly label?: string;
  readonly className?: string;
}

const HEIGHT = { sm: 'h-1', md: 'h-2' } as const;

export function ProgressBar({ value, max = 100, size = 'md', label, className }: ProgressBarProps) {
  const { t } = useI18n();
  const determinate = value !== undefined;
  const pct = determinate ? Math.min(100, Math.max(0, (value / max) * 100)) : undefined;
  return (
    <div
      role="progressbar"
      aria-label={label ?? t('common:status.progress', { defaultValue: 'Progress' })}
      aria-valuemin={determinate ? 0 : undefined}
      aria-valuemax={determinate ? max : undefined}
      aria-valuenow={determinate ? value : undefined}
      className={cn(
        'relative w-full overflow-hidden rounded-full bg-hairline',
        HEIGHT[size],
        className,
      )}
    >
      {determinate ? (
        <div
          className="h-full bg-accent transition-[width] duration-(--duration-quick) ease-(--ease-out) motion-reduce:transition-none"
          style={{ width: `${pct}%` }}
        />
      ) : (
        <div
          className="absolute inset-y-0 w-1/3 bg-accent rounded-full motion-safe:animate-bravemove-progress"
          style={{ insetInlineStart: '33%' }}
        />
      )}
    </div>
  );
}
